/**
 * Global Utility Functions Hook
 * Centralized utility functions for common operations across the application
 * Uses global configuration context automatically
 */

import { useGlobalConfig } from "@finitless/shared/contexts";
import { parseISO, format, isValid, parse } from "date-fns";

/**
 * Parse a number with custom formatting options
 * @param {number|string} value - The value to parse
 * @param {Object} options - Formatting options
 * @param {number} options.decimal - Number of decimal places (default: 2)
 * @param {string} options.separator - Decimal separator (default: ',')
 * @param {string} options.thousand - Thousand separator (default: '.')
 * @returns {string} Formatted number string
 */
export const parseNumber = (value, options = {}) => {
  // Default configuration
  const defaultConfig = {
    decimal: 2,
    separator: ".",
    thousand: ",",
  };

  // Merge with provided options
  const formatNumber = {
    decimal: options?.decimal ?? defaultConfig.decimal,
    separator: options?.separator ?? defaultConfig.separator,
    thousand: options?.thousand ?? defaultConfig.thousand,
  };

  // Parse the value to float
  value = parseFloat(value) || 0;

  // Format to fixed decimal places
  let number = value.toFixed(formatNumber.decimal);
  number = number.toString();

  // Replace decimal point with custom separator
  if (number.indexOf(".") !== -1) {
    number = number.replace(".", formatNumber.separator);
  } else if (number.indexOf(",") !== -1) {
    number = number.replace(",", formatNumber.separator);
  }

  // Split by decimal separator
  const numberParts = number.split(formatNumber.separator);

  // Handle thousand separator
  if (formatNumber.thousand === "space") {
    formatNumber.thousand = " ";
  }

  // Add thousand separators to integer part
  numberParts[0] = numberParts[0].replace(
    /(.)(?=(\d{3})+$)/g,
    "$1" + formatNumber.thousand,
  );

  // Join parts back together
  number = numberParts.join(formatNumber.separator);

  return number;
};

/**
 * Hook for global utility functions with automatic global configuration
 * @returns {Object} Object containing utility functions and loading states
 */
export const useGlobalUtils = () => {
  const { getConfigValue, isLoading, isError, isReady, globalConfigs } =
    useGlobalConfig();

  /**
   * Parse a price with currency formatting using global configuration
   * @param {number|string} value - The value to parse
   * @param {Object} options - Formatting options
   * @param {number} options.decimal - Number of decimal places (default: 2)
   * @param {string} options.separator - Decimal separator (default: '.')
   * @param {string} options.thousand - Thousand separator (default: ',')
   * @param {string} options.currency - Currency symbol (uses global config if not provided)
   * @param {string} options.currencyPosition - Currency position 'left' or 'right' (default: 'left')
   * @returns {string} Formatted price string
   */
  const parsePrice = (value, options = {}) => {
    // Default configuration
    const defaultConfig = {
      decimal: 2,
      separator: ".",
      thousand: ",",
      currency: "$",
      currencyPosition: "left",
    };

    // Merge with provided options, using global config for currency symbol
    const formatNumber = {
      decimal: options?.decimal ?? defaultConfig.decimal,
      separator: options?.separator ?? defaultConfig.separator,
      thousand: options?.thousand ?? defaultConfig.thousand,
      currency:
        options?.currency ??
        getConfigValue("format_number_currency", defaultConfig.currency),
      currencyPosition:
        options?.currencyPosition ?? defaultConfig.currencyPosition,
    };

    // Parse the number first
    let number = parseNumber(value, formatNumber);

    // Add currency symbol
    if (formatNumber.currencyPosition === "left") {
      number = formatNumber.currency + " " + number;
    } else {
      number = number + " " + formatNumber.currency;
    }

    return number;
  };

  /**
   * Parse a date using global configuration for time format
   * @param {string|Date|number} date - The date to parse
   * @param {Object} options - Formatting options
   * @param {string} options.formatTime - Time format '12' or '24' (uses global config if not provided)
   * @param {Array<string>} options.inputFormat - Array of input formats to try
   * @param {string} options.outputFormat - Output format pattern
   * @param {string} options.fallbackText - Text to return for invalid dates
   * @param {boolean} options.useUTC - Whether to display time in UTC
   * @returns {string} Formatted date string
   */
  const parseDate = (date, options = {}) => {
    // Default configuration
    const defaultConfig = {
      formatTime: "24",
      inputFormat: [
        "yyyy-MM-dd HH:mm:ss",
        "yyyy-MM-dd hh:mm:ss a",
        "yyyy-MM-dd hh:mm:ss",
      ],
      outputFormat: null,
      fallbackText: "Invalid format",
      useUTC: false,
    };

    // Merge with provided options, using global config for time format
    const config = {
      formatTime:
        options?.formatTime ??
        (getConfigValue("general_hour_format", "HH:mm").includes("HH")
          ? "24"
          : "12"),
      inputFormat: options?.inputFormat ?? defaultConfig.inputFormat,
      outputFormat:
        options?.outputFormat ??
        getConfigValue("dates_general_format", "yyyy-MM-dd HH:mm") ??
        defaultConfig.outputFormat,
      fallbackText: options?.fallbackText ?? defaultConfig.fallbackText,
      useUTC: options?.useUTC ?? defaultConfig.useUTC,
    };

    // Set default output format based on formatTime if not provided
    if (!config.outputFormat) {
      config.outputFormat =
        config.formatTime === "24" ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd hh:mm a";
    }

    // Handle null, undefined, or empty date
    if (!date || date === "") {
      return config.fallbackText;
    }

    let parsedDate = null;

    // If it's already a Date object, use it directly
    if (date instanceof Date) {
      parsedDate = isValid(date) ? date : null;
    }
    // If it's a number (timestamp), convert to Date
    else if (typeof date === "number") {
      parsedDate = new Date(date);
      if (!isValid(parsedDate)) {
        parsedDate = null;
      }
    }
    // If it's a string, try to parse it
    else if (typeof date === "string") {
      // Try parseISO first
      try {
        parsedDate = parseISO(date);
        if (!isValid(parsedDate)) {
          parsedDate = null;
        }
      } catch {
        parsedDate = null;
      }

      // If parseISO failed, try custom formats
      if (!parsedDate) {
        for (const formatStr of config.inputFormat) {
          try {
            parsedDate = parse(date, formatStr, new Date());
            if (isValid(parsedDate)) {
              break;
            }
          } catch {
            continue;
          }
        }
      }
    }

    // If no valid date was parsed, return fallback text
    if (!parsedDate || !isValid(parsedDate)) {
      return config.fallbackText;
    }

    // If useUTC, convert parsed date to UTC then to local
    if (config.useUTC && typeof date === "string") {
      const isoString = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss");
      parsedDate = new Date(isoString + "Z");
    }

    // Format the final date
    try {
      return format(
        parsedDate,
        config.outputFormat?.replace("YYYY", "yyyy")?.replace("DD", "dd"),
      );
    } catch (error) {
      console.warn("Date formatting failed:", error);
      return config.fallbackText;
    }
  };

  const calculateEstimatedDelivery = (order) => {
    const deliveryTypes = [1, 7];
    let totalEta = 0;

    if (order?.delivered_in) totalEta += order.delivered_in;
    if (order?.prepared_in) totalEta += order.prepared_in;
    if (
      deliveryTypes?.includes(order?.delivery_type) &&
      order?.eta_drive_time
    ) {
      totalEta += order.eta_drive_time;
    }

    const _delivery = order?.delivery_datetime_utc || order?.delivery_datetime;

    if (!_delivery) return "N/A";

    if (order?.eta_current_status_time) {
      const currentStatueEta = order.eta_current_status_time;
      totalEta += currentStatueEta;

      let previousStatusTimes = 0;
      if (order?.eta_previous_status_times) {
        Object.keys(order.eta_previous_status_times).forEach((key) => {
          if (!key.includes("status_penalty")) {
            previousStatusTimes += order.eta_previous_status_times[key];
          }
        });
      }
      totalEta += previousStatusTimes;

      let nextStatusTimes = 0;
      if (order?.eta_next_status_times) {
        Object.keys(order.eta_next_status_times).forEach((key) => {
          if (!key.includes("status_penalty")) {
            nextStatusTimes += order.eta_next_status_times[key];
          }
        });
      }
      totalEta += nextStatusTimes;

      const statusTimestamp =
        order?.reporting_data?.at?.[`status:${order.status}`];
      if (statusTimestamp) {
        const statusDate = new Date(statusTimestamp + "Z"); // Parse as UTC
        const targetTime = new Date(
          statusDate.getTime() + order.eta_current_status_time * 60 * 1000,
        );
        const diffTimeAsSeconds = (targetTime - new Date()) / 1000;
        const diffTimeAsMinutes = Math.ceil(diffTimeAsSeconds / 60);

        if (diffTimeAsMinutes <= 0) {
          totalEta +=
            Math.floor(
              Math.abs(diffTimeAsMinutes / order.eta_current_status_time) + 1,
            ) * order.eta_current_status_penalty_time;
        }
      }
    } else {
      totalEta = (order?.eta_time || 0) + totalEta;
    }

    // If status is 13 (delivered), show delivery time as-is
    if (order?.status === 13) {
      return parseDate(_delivery, { useUTC: !!order?.delivery_datetime_utc });
    }

    // Calculate estimated delivery time
    let deliveryDate = new Date(
      _delivery + (order?.delivery_datetime_utc ? "Z" : ""),
    );
    deliveryDate.setMinutes(deliveryDate.getMinutes() + totalEta);

    return deliveryDate;
  };

  return {
    // Main utility functions with global config
    parsePrice,
    parseDate,
    calculateEstimatedDelivery,

    // Loading states
    isLoading,
    isError,
    isReady,

    // Raw global configs for advanced usage
    globalConfigs,
  };
};
/**
 * Format a number with thousand separators
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  return parseNumber(value, {
    decimal: decimals,
    separator: ".",
    thousand: ",",
  });
};

/**
 * Format a percentage
 * @param {number|string} value - The value to format (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  const num = parseFloat(value) || 0;
  return num.toFixed(decimals) + "%";
};

/**
 * Format a large number with K, M, B suffixes
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted number with suffix
 */
export const formatCompactNumber = (value, decimals = 1) => {
  const num = parseFloat(value) || 0;

  if (num >= 1e9) {
    return (num / 1e9).toFixed(decimals) + "B";
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(decimals) + "M";
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(decimals) + "K";
  }

  return num.toString();
};

/**
 * Capitalize the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} Capitalized string
 */
export const capitalize = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Convert a string to title case
 * @param {string} str - The string to convert
 * @returns {string} Title case string
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
};

/**
 * Truncate a string to a specified length
 * @param {string} str - The string to truncate
 * @param {number} length - Maximum length (default: 50)
 * @param {string} suffix - Suffix to add (default: '...')
 * @returns {string} Truncated string
 */
export const truncate = (str, length = 50, suffix = "...") => {
  if (!str || typeof str !== "string") return "";
  if (str.length <= length) return str;
  return str.substring(0, length) + suffix;
};

/**
 * Truncate text to a specified maximum length with intelligent URL handling
 *
 * For URLs, this function intelligently truncates long subdomains while preserving
 * the protocol, domain, and path. If the subdomain is longer than 10 characters,
 * it shows only the first 2 and last 2 characters of the subdomain.
 *
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum length for the result (default: 30)
 * @returns {string} Truncated text with '...' suffix if needed
 */
export const truncateUrl = (text, maxLength = 30) => {
  if (!text || typeof text !== "string") return "";

  if (text.length <= maxLength) return text;

  const match = text.match(/^(https?:\/\/)([^/]+)(\/?.*)?$/);

  if (match) {
    const [, protocol, host, path] = match;
    const pathString = path || "";

    const firstDotIndex = host.indexOf(".");

    if (firstDotIndex !== -1) {
      const subdomain = host.substring(0, firstDotIndex);
      const domainSuffix = host.substring(firstDotIndex);

      if (subdomain.length > 10) {
        const start = subdomain.substring(0, 2);
        const end = subdomain.substring(subdomain.length - 2);

        const truncatedSubdomain = `${start}...${end}`;

        return `${protocol}${truncatedSubdomain}${domainSuffix}${pathString}`;
      }
    }
  }

  return `${text.substring(0, maxLength - 3)}...`;
};

/**
 * Convert arbitrary text into a URL-safe slug
 * Reused across forms (business, brand/franchise, etc.) for consistency
 * @param {string} text - Source text
 * @returns {string} slugified string
 */
export const stringToSlug = (text) => {
  if (!text || typeof text !== "string") return "";
  let name = text.replace(/^\s+|\s+$/g, "");
  name = name.toLowerCase();

  // remove accents, swap ñ for n, etc
  const from = "åàáãäâèéëêìíïîòóöôùúüûñç·/_,:;";
  const to = "aaaaaaeeeeiiiioooouuuunc------";
  for (let i = 0, l = from.length; i < l; i++) {
    name = name.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  name = name
    .replace(/[^a-z0-9 -]/g, "") // remove invalid chars
    .replace(/\s+/g, "_") // collapse whitespace and replace by _
    .replace(/-+/g, "_") // collapse dashes
    .replace(/^-+/, "") // trim - from start of text
    .replace(/-+$/, ""); // trim - from end of text

  return name;
};

/**
 * Debounce a function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Build query parameters for API endpoints
 * @param {Object} params - Parameters object
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 * @returns {string} Query string
 */
/**
 * Builds query parameters string from any object
 * Handles arrays, objects, booleans, numbers, strings, and null/undefined values
 * @param {Object} params - Object containing query parameters
 * @returns {string} - URL query string
 */
export const buildQueryParams = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Helper function to handle different value types
  const addParam = (key, value) => {
    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }

    if (typeof value === "boolean") {
      queryParams.append(key, value.toString());
    } else if (typeof value === "number") {
      queryParams.append(key, value.toString());
    } else if (typeof value === "string") {
      if (value.trim() !== "") {
        // Skip empty strings
        queryParams.append(key, value);
      }
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        // Handle arrays - join with comma or stringify based on content
        if (
          value.every(
            (item) => typeof item === "string" || typeof item === "number",
          )
        ) {
          queryParams.append(key, value.join(","));
        } else {
          queryParams.append(key, JSON.stringify(value));
        }
      }
    } else if (typeof value === "object") {
      // Handle objects by stringifying them
      queryParams.append(key, JSON.stringify(value));
    }
  };

  // Process all parameters dynamically
  Object.entries(params).forEach(([key, value]) => {
    addParam(key, value);
  });

  return queryParams.toString();
};

/**
 * Convert time between different units
 * @param {number|string} value - The time value to convert
 * @param {string} fromUnit - Source unit ('ms', 's', 'm', 'h', 'd', 'w', 'mo', 'y')
 * @param {string} toUnit - Target unit ('ms', 's', 'm', 'h', 'd', 'w', 'mo', 'y')
 * @param {Object} options - Conversion options
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @param {boolean} options.round - Whether to round the result (default: false)
 * @param {boolean} options.includeUnit - Whether to include unit in result (default: true)
 * @returns {string|number} Converted time value with or without unit
 */
export const convertTime = (value, fromUnit, toUnit, options = {}) => {
  const { decimals = 2, round = false, includeUnit = true } = options;

  // Validate input
  if (value == null || value === "" || isNaN(value)) {
    return includeUnit ? `0${toUnit}` : 0;
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return includeUnit ? `0${toUnit}` : 0;
  }

  // Define conversion factors to milliseconds (base unit)
  const conversionFactors = {
    ms: 1,
    s: 1000,
    min: 60 * 1000,
    h: 60 * 60 * 1000,
    d: 24 * 60 * 60 * 1000,
    w: 7 * 24 * 60 * 60 * 1000,
    mo: 30 * 24 * 60 * 60 * 1000, // Approximate month (30 days)
    y: 365 * 24 * 60 * 60 * 1000, // Approximate year (365 days)
  };

  // Validate units
  if (!conversionFactors[fromUnit] || !conversionFactors[toUnit]) {
    return includeUnit ? `${numValue}${toUnit}` : numValue;
  }

  // Convert to milliseconds first
  const milliseconds = numValue * conversionFactors[fromUnit];

  // Convert from milliseconds to target unit
  const result = milliseconds / conversionFactors[toUnit];

  // Apply formatting
  let finalResult;
  if (round) {
    finalResult = Math.round(result);
  } else {
    finalResult = parseFloat(result.toFixed(decimals));
  }

  // Return with or without unit
  return includeUnit ? `${finalResult}${toUnit}` : finalResult;
};

/**
 * Convert a file to base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} Promise that resolves with the base64 string
 */
export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Export all utilities as default object
export default {
  useGlobalUtils,
  parseNumber,
  formatNumber,
  formatPercentage,
  formatCompactNumber,
  capitalize,
  toTitleCase,
  truncate,
  truncateUrl,
  stringToSlug,
  debounce,
  buildQueryParams,
  convertTime,
  convertFileToBase64,
};
